/**
 * The "Følger året" theme: the colours follow the season, and a little cosiness is sprinkled over
 * it — the season's own band at the bottom and a few falling (or rising) particles, or on a
 * holiday the holiday's decorations along all three edges.
 */
export type Season = 'winter' | 'spring' | 'summer' | 'autumn';
import { moonPhase } from './moon-phase';

export type Holiday =
  | 'fastelavn'
  | 'easter'
  | 'sankthans'
  | 'halloween'
  | 'christmas'
  | 'newyear'
  | 'eid'
  | 'lunarnewyear'
  | 'diwali';
export type Particles = 'snow' | 'petal' | 'leaf' | 'spark' | 'ember' | 'confetti';

export interface YearScene {
  season: Season;
  holiday: Holiday | null;
  particles: Particles;
}

/** The meteorological seasons, as Danes speak of them: winter is December to February. */
export function season(date: Date): Season {
  const month = date.getMonth();
  if (month === 11 || month <= 1) return 'winter';
  if (month <= 4) return 'spring';
  if (month <= 7) return 'summer';
  return 'autumn';
}

/** Easter Sunday in `year` (the Gregorian computus, as in "Meeus/Jones/Butcher"). */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31);
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

const DAY_MS = 86_400_000;

/** Whole days from `from` to `to` (both taken at midnight, so summer time does not count). */
function daysBetween(from: Date, to: Date): number {
  const a = Date.UTC(from.getFullYear(), from.getMonth(), from.getDate());
  const b = Date.UTC(to.getFullYear(), to.getMonth(), to.getDate());
  return Math.round((b - a) / DAY_MS);
}

/** The Islamic (Umm al-Qura) calendar's month and day, or null where the browser lacks it. */
const HIJRI = (() => {
  try {
    const format = new Intl.DateTimeFormat('en-u-ca-islamic-umalqura', { month: 'numeric', day: 'numeric' });
    return format.resolvedOptions().calendar === 'islamic-umalqura' ? format : null;
  } catch {
    return null;
  }
})();

/**
 * Eid: Eid al-Fitr, 1–3 Shawwal, after Ramadan — the Eid best known and most widely kept in Denmark
 * (Eid al-Adha is left out). By the Umm al-Qura calendar — a mosque here may start a day earlier or
 * later, after the moon is sighted.
 */
export function isEid(date: Date): boolean {
  if (!HIJRI) return false;
  // At noon, so the day is the same whatever the time zone's offset.
  const parts = HIJRI.formatToParts(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12));
  const month = Number(parts.find((p) => p.type === 'month')?.value);
  const day = Number(parts.find((p) => p.type === 'day')?.value);
  return month === 10 && day <= 3;
}

/** The Chinese calendar's month and day, or null where the browser lacks it. */
const CHINESE = (() => {
  try {
    const format = new Intl.DateTimeFormat('en-u-ca-chinese', { month: 'numeric', day: 'numeric' });
    return format.resolvedOptions().calendar === 'chinese' ? format : null;
  } catch {
    return null;
  }
})();

function chineseMonthDay(date: Date): { month: number; day: number } | null {
  if (!CHINESE) return null;
  const parts = CHINESE.formatToParts(new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12));
  return {
    month: Number(parts.find((p) => p.type === 'month')?.value),
    day: Number(parts.find((p) => p.type === 'day')?.value),
  };
}

/**
 * Chinese (lunar) New Year — also Vietnamese Tết and Korean Seollal: from New Year's Eve to the third
 * day of the first month, by the Chinese calendar.
 */
export function isLunarNewYear(date: Date): boolean {
  const today = chineseMonthDay(date);
  if (!today) return false;
  if (today.month === 1 && today.day <= 3) return true;
  const tomorrow = chineseMonthDay(new Date(date.getFullYear(), date.getMonth(), date.getDate() + 1));
  return tomorrow?.month === 1 && tomorrow.day === 1;
}

/**
 * Diwali, the festival of lights: the five days around the new moon of Kartika — the new moon that
 * falls between mid-October and mid-November. From the moon's mean cycle, so a day either way of
 * the almanac; the five days cover it.
 */
export function isDiwali(date: Date): boolean {
  const noon = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 12);
  const phase = moonPhase(noon);
  const toNewMoon = (phase < 0.5 ? -phase : 1 - phase) * 29.530588853;
  if (Math.abs(toNewMoon) > 2.5) return false;
  const newMoon = new Date(noon.getTime() + toNewMoon * 86_400_000);
  const month = newMoon.getMonth();
  const day = newMoon.getDate();
  return (month === 9 && day >= 14) || (month === 10 && day <= 16);
}

/**
 * The holidays the theme decorates for, and how long:
 * fastelavn Friday–Monday around Fastelavnssøndag (seven weeks before Easter), Easter Palm Sunday
 * to Easter Monday, Sankt Hans 22–24 June, Halloween 25–31 October, Christmas 1–26 December and
 * New Year 31 December–1 January; Eid (`isEid()`), Chinese New Year
 * (`isLunarNewYear()`) and Diwali (`isDiwali()`), whose days move through the year. A screen can
 * leave out any of them (`hidden`); then those days are the season's.
 */
export function holiday(date: Date, hidden: readonly Holiday[] = []): Holiday | null {
  const h = calendarHoliday(date);
  return h && !hidden.includes(h) ? h : null;
}

function calendarHoliday(date: Date): Holiday | null {
  const month = date.getMonth();
  const day = date.getDate();
  if ((month === 11 && day === 31) || (month === 0 && day === 1)) return 'newyear';
  if (month === 11 && day <= 26) return 'christmas';
  if (month === 9 && day >= 25) return 'halloween';
  if (month === 5 && day >= 22 && day <= 24) return 'sankthans';

  const fromEaster = daysBetween(easterSunday(date.getFullYear()), date);
  if (fromEaster >= -7 && fromEaster <= 1) return 'easter';
  if (fromEaster >= -51 && fromEaster <= -48) return 'fastelavn';
  if (isEid(date)) return 'eid';
  if (isLunarNewYear(date)) return 'lunarnewyear';
  if (isDiwali(date)) return 'diwali';
  return null;
}

const SEASON_PARTICLES: Record<Season, Particles> = {
  winter: 'snow',
  spring: 'petal',
  summer: 'spark',
  autumn: 'leaf',
};

const HOLIDAY_PARTICLES: Record<Holiday, Particles> = {
  fastelavn: 'confetti',
  easter: 'petal',
  sankthans: 'ember',
  halloween: 'leaf',
  christmas: 'snow',
  newyear: 'confetti',
  eid: 'spark',
  lunarnewyear: 'confetti',
  diwali: 'spark',
};

export function yearScene(date: Date, hidden: readonly Holiday[] = []): YearScene {
  const s = season(date);
  const h = holiday(date, hidden);
  return { season: s, holiday: h, particles: h ? HOLIDAY_PARTICLES[h] : SEASON_PARTICLES[s] };
}

/** Particles that rise (sparks, embers) instead of falling. */
export function particlesRise(particles: Particles): boolean {
  return particles === 'spark' || particles === 'ember';
}

/**
 * The particle slots: where each starts across the screen, how long it takes, how far into its
 * fall it starts (so they do not all start together) and its size. Fixed, so a screen looks the
 * same on every reload; a dozen is "a little".
 */
export const PARTICLE_SLOTS: readonly { x: number; duration: number; delay: number; size: number }[] = [
  { x: 4, duration: 26, delay: 3, size: 1 },
  { x: 13, duration: 34, delay: 19, size: 0.7 },
  { x: 21, duration: 29, delay: 8, size: 1.2 },
  { x: 30, duration: 38, delay: 27, size: 0.8 },
  { x: 38, duration: 24, delay: 14, size: 1 },
  { x: 47, duration: 31, delay: 2, size: 0.9 },
  { x: 55, duration: 36, delay: 22, size: 1.1 },
  { x: 63, duration: 27, delay: 11, size: 0.7 },
  { x: 71, duration: 33, delay: 30, size: 1 },
  { x: 79, duration: 25, delay: 6, size: 1.2 },
  { x: 87, duration: 37, delay: 17, size: 0.8 },
  { x: 95, duration: 30, delay: 25, size: 1 },
];
